import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import {
  Text,
  IndexTable,
  Badge,
  Thumbnail,
  Button,
  Tooltip,
} from '@shopify/polaris';

import { ViewIcon, BarcodeIcon } from '@shopify/polaris-icons';

import { findBarcodeImage } from '../../redux/actions/products';
import { downloadBarcodeImage } from '../../redux/actions/barcodes';
import LoadingSpinner from '../partials/LoadingSpinner';

const SkuLine = ({
  shopUrl,
  payload,
  setDownloadSubmitted,
  selectedVariantList,
  index,
  id,
  category,
  featuredMedia,
  producType,
  status,
  tags,
  title,
  totalInventory,
  tracksInventory,
  type,
  variants,
  vendor,
  barcode,
  inventoryQuantity,
  displayName,
  parent,
  price,
  selectedOptions,
  sku,
  downloadUrl,
  products,
  downloadBarcode,
  loadBarcodeImage,
  toggleProduct,
  toggleVariant,
  productSelections,
  settingsHash,
  selectedLocation,
  availableLocations,
  inventoryLevels,
  searchedProducts
}) => {
  const [localDownloadSubmitted, setLocalDownloadSubmitted] = useState(false);

  const findImage = () => {
    let symbol = sku;
    if (settingsHash.barcode_image_generation_field === 'barcode') {
      symbol = barcode;
    }
    loadBarcodeImage(shopUrl, payload, id, symbol);
  };

  useEffect(() => {
    let symbol = sku;
    if (settingsHash.barcode_image_generation_field === 'barcode') {
      symbol = barcode;
    }

    if(symbol && (products.searchedBarcodes.indexOf(symbol) == -1) && (products.searchingBarcodes.indexOf(symbol) == -1) && (products.missingBarcodes.indexOf(symbol) == -1)) {
      findImage();
    }
  }, [findImage]);

  useEffect(() => {
    if(downloadUrl) { setLocalDownloadSubmitted(false); }
  }, [downloadUrl]);

  const downloadImage = (shopUrl, payload, itemId, sku, barcode) => {
    setDownloadSubmitted(true);
    setLocalDownloadSubmitted(true);

    let symbol = sku;
    if (settingsHash.barcode_image_generation_field === 'barcode') {
      symbol = barcode;
    }

    downloadBarcode(shopUrl, payload, itemId, symbol);
  }

  const switchSelection = () => {
    if (type === 'product') {
      toggleProduct(id);
    }

    if (type === 'variant') {
      let relatedProduct = searchedProducts.find((product) => product.id === parent);

      toggleVariant({
        id: id,
        parent: parent,
        sku: sku,
        barcode: barcode,
        featuredMedia: featuredMedia,
        name: relatedProduct.title,
        options: selectedOptions,
        price: price,
        quantity: inventoryQuantity || totalInventory
      });
    }
  }

  if(selectedLocation !== 'any') {
    if(type === 'product' && availableLocations.indexOf(selectedLocation) === -1) {
      return <></>
    }

    if(type === 'variant' && Object.keys(inventoryLevels).indexOf(selectedLocation) === -1) {
      return <></>
    }
  }

  return (
    <IndexTable.Row
      id={id}
      key={id}
      selected={productSelections[id] || selectedVariantList.find((variant) => variant.id === id)}
      position={index}
      rowType={type === 'variant' ? 'child' : 'data'}
      tone={type === 'variant' ? '' : 'subdued'}
      onClick={switchSelection}
    >
      <IndexTable.Cell>
        { (featuredMedia?.preview?.image?.url) && <Thumbnail
          source={featuredMedia?.preview?.image?.url}
          size="small"
          alt={featuredMedia?.alt}/> }
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {displayName || title} &nbsp;
          { status === 'ACTIVE' &&
            <Badge 
              tone="success"
            >
              Active
            </Badge>
          }
          { status !== 'ACTIVE' && status &&
            <Badge 
              tone="warning"
            >
              { status }
            </Badge>
          }
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" alignment="end" numeric>
          {inventoryQuantity}
        </Text>
      </IndexTable.Cell>

      <IndexTable.Cell>
        {tags?.map(tag => 
          <span key={tag}>
            <Badge 
              tone="info"
              key={tag}
            >
              {tag}
            </Badge>
             &nbsp;
          </span>
        )}
        {selectedOptions?.map(option =>
          <div key={option.name}>
            <Badge 
              tone="info"
              key={option.name}
            >
              <Text>{option.value}</Text>
            </Badge>
             &nbsp;
          </div>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        { (products.searchingBarcodes.indexOf(settingsHash.barcode_image_generation_field === 'barcode' ? barcode : sku) > -1) &&
          <LoadingSpinner size="small" statusText="Finding barcode..." />
        }

        { (products.searchedBarcodes.indexOf(settingsHash.barcode_image_generation_field === 'barcode' ? barcode : sku) > -1) &&
          <>
            <Button icon={BarcodeIcon} loading={localDownloadSubmitted} disabled={localDownloadSubmitted} onClick={() => downloadImage(shopUrl, payload, id, sku, barcode)}>
              View Barcode Image
            </Button>
            &nbsp;
            <Link to={`/barcode_actions/${settingsHash.barcode_image_generation_field === 'barcode' ? barcode : sku}${window.location.search}`}>
              <Button icon={ViewIcon}>Preview Labels</Button>
            </Link>
          </>
        }

        { (type === 'variant') && ((settingsHash.barcode_image_generation_field === 'barcode' && !barcode) || (settingsHash.barcode_image_generation_field === 'sku' && !sku)) &&
          <Tooltip content={`A barcode image will not generate for this variant because the ${settingsHash.barcode_image_generation_field} is missing.`} dismissOnMouseOut>
            <Badge tone="warning">
              <Text>{settingsHash.barcode_image_generation_field} value missing</Text>
            </Badge>
          </Tooltip>
        }
      </IndexTable.Cell>
    </IndexTable.Row>
  )
}

const mapStateToProps = state => ({
  products: state.products,
  downloadUrl: state.barcodes.downloadUrl,
  settingsHash: state.settings.settings,
  searchedProducts: state.products.searchedProducts,
});

const mapDispatchToProps = (dispatch) => ({
  downloadBarcode: (shopUrl, payload, itemId, symbol) => dispatch(downloadBarcodeImage(shopUrl, payload, itemId, symbol)),
  loadBarcodeImage: (shopUrl, payload, itemId, symbol) => dispatch(findBarcodeImage(shopUrl, payload, itemId, symbol)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SkuLine);